import { http } from '@/api/http';
import type { PageEnvelope } from '@/api/shared';

/**
 * Raw transport surface for the admin S3-config feature (PTV2-1383): CRUD over per-tenant S3
 * blob-store credentials. Hand-written slice DTOs (snake_case) — the api-layer mapper normalises to
 * camelCase view models. The stored {@code secret_access_key} is write-only: sent on create/update,
 * never returned; {@code has_secret} tells the UI whether one is stored. When {@code folder} is
 * blank the backend prefixes objects with the tenant id, so a tenant only ever reads/writes under
 * its own folder.
 */

/** Wire DTO for an S3 config. All fields optional on the wire; the mapper normalises. */
export interface S3ConfigDto {
  id?: string;
  name?: string;
  bucket_name?: string;
  region?: string;
  access_key_id?: string | null;
  /** Write-only — never present on reads. */
  secret_access_key?: string | null;
  /** Read-only — whether a secret is stored. */
  has_secret?: boolean;
  /** Optional S3-compatible/MinIO endpoint. Blank = real AWS S3. */
  endpoint_url?: string | null;
  /** Optional key prefix. Blank = resolves to the tenant id at use. */
  folder?: string | null;
  is_default?: boolean;
  active?: boolean;
}

export type S3ConfigPageDto = PageEnvelope<S3ConfigDto>;

function encode(id: string): string {
  return encodeURIComponent(id);
}

export const adminS3Endpoints = {
  /** GET /s3-configs — paginated list for the current tenant. */
  getS3Configs: (params?: {
    page?: number;
    size?: number;
    active?: boolean;
    signal?: AbortSignal;
  }): Promise<S3ConfigPageDto> => {
    const search = new URLSearchParams();
    if (params?.page !== undefined) search.set('page', String(params.page));
    if (params?.size !== undefined) search.set('size', String(params.size));
    if (params?.active !== undefined) search.set('active', String(params.active));
    const query = search.toString();
    const path = query.length > 0 ? `/s3-configs?${query}` : '/s3-configs';
    return http.get<S3ConfigPageDto>(
      path,
      params?.signal !== undefined ? { signal: params.signal } : undefined,
    );
  },

  /** POST /s3-configs — create. 409 if the name is taken; 422 if the credentials are missing. */
  createS3Config: (body: S3ConfigDto, options?: { signal?: AbortSignal }): Promise<S3ConfigDto> =>
    http.post<S3ConfigDto>('/s3-configs', body, options),

  /** PUT /s3-configs/{id} — update. A blank secret keeps the stored one. */
  updateS3Config: (
    id: string,
    body: S3ConfigDto,
    options?: { signal?: AbortSignal },
  ): Promise<S3ConfigDto> => http.put<S3ConfigDto>(`/s3-configs/${encode(id)}`, body, options),

  /** DELETE /s3-configs/{id}. */
  deleteS3Config: (id: string, options?: { signal?: AbortSignal }): Promise<undefined> =>
    http.delete<undefined>(`/s3-configs/${encode(id)}`, options),
};
